import React from 'react'
import { cn } from '../../../lib/utils'
import MeetingInfo from './MeetingInfo'
import { useBenefits } from '../BenefitsProvider'
import { useTranslation } from '~/hooks/useTranslation'
import { LinkButton } from '~/lib/ui/button'
import 'moment-business-days'
import { icons } from '../ServiceRequestIcons'
import HeadshotCircle from '../MeetYourCoach/HeadshotCircle'
import { useFormatTime } from '~/hooks/useFormatTime'
import FeedbackCarousel from './FeedbackCarousel'

export default function ServiceCard({
  requestType,
  title,
  description,
  buttonText,
  iconType,
  fullWidth,
  photoUrl,
  coachCard,
  classes,
  liaison,
  buttonUrl,
  buttonChild,
  buttonChildShow,
  introCallServiceType,
  isIntroCard,
  disableLeftAlignment,
  ...props
}) {
  const { t } = useTranslation('service_card')

  const { serviceRequestList, select, resumeReview, coach } = useBenefits()
  const activeCoachId = coach?.id

  const serviceRequest = serviceRequestList[requestType]
  const {
    calendly_event: calendlyEvent,
    completed: requestComplete,
    completed_at: requestCompletedAt,
    created_at: requestCreatedAt,
    due_at: requestDueAt,
    status: requestStatus,
    coach: { first_name: coachName, id: serviceRequestCoachId } = {},
    meeting_via_calendly: meetingViaCalendly,
    cancelled_by_coach: cancelledByCoach,
    coach_started: coachStarted,
    clifton_strengths_code: strengthsCode,
    is_cancelled_by_coach_change: isCancelledByCoachChange,
  } = serviceRequest || {}

  const completedReoccurringRequests =
    serviceRequest?.completed_reoccurring_requests
  const hasCompletedReoccurringRequests =
    completedReoccurringRequests && completedReoccurringRequests.length > 0
  const noShow = calendlyEvent?.service_request_no_show_id
  const coachChange =
    calendlyEvent?.cancelled_by_coach_change || isCancelledByCoachChange
  const isRequestPending = requestStatus === 'assigned'
  const whiteText = select && classes !== 'bg-intoo-blue-light'
  const textColor = whiteText ? 'text-white' : ''

  const isSelectIntroCall = select && requestType === introCallServiceType

  const callScheduled = calendlyEvent && calendlyEvent['start_time']
  const isResumeReviewCard = requestType === 'ServiceRequest::ResumeReview'
  const returnedStatus =
    isResumeReviewCard &&
    (resumeReview?.status === 'returned' ||
      resumeReview?.status === 'revision_returned' ||
      resumeReview?.status === 'final_revision_returned')
  const showScheduledMeeting = callScheduled && !returnedStatus
  const showMeetingStatus =
    showScheduledMeeting || cancelledByCoach || requestComplete
  const meetingScheduled = calendlyEvent && !noShow && showScheduledMeeting
  const meetingIsCancelled = requestStatus === 'cancelled'
  const needsScheduling =
    meetingViaCalendly && !calendlyEvent?.start_time && !meetingIsCancelled

  return (
    <Wrapper
      eventName="Meeting"
      callScheduled={callScheduled}
      isSelectIntroCall={isSelectIntroCall}
      className={cn(
        'flex flex-col rounded-2xl p-5',
        classes,
        fullWidth && 'col-span-1 md:col-span-2',
      )}
      requestComplete={requestComplete}
      {...props}
    >
      <div
        className={cn(
          'flex flex-col items-center justify-between gap-6 md:flex-row',
        )}
      >
        <div className={cn('service-card flex-1', textColor)}>
          <div
            className={cn(
              'flex items-center gap-3',
              requestComplete ? textColor + ' -mb-4' : textColor,
            )}
          >
            <IconSection
              requestComplete={requestComplete}
              icons={icons}
              iconType={iconType}
            />
            <div className="subtitle !text-xl">{title}</div>
            {showMeetingStatus && (
              <StatusIndicator
                calendlyEvent={calendlyEvent}
                requestStatus={requestStatus}
                requestCompletedAt={requestCompletedAt}
                coachStarted={coachStarted}
                select={select}
              />
            )}
          </div>

          {!cancelledByCoach || serviceRequest?.completed ? (
            <p className="mt-2 flex-grow">{description}</p>
          ) : null}

          <div className="mt-3">
            {meetingScheduled && !requestComplete ? (
              <MeetingInfo
                calendlyEvent={calendlyEvent}
                requestType={requestType}
                buttonText={buttonText}
                requestComplete={requestComplete}
                hasCompletedReoccurringRequests={
                  hasCompletedReoccurringRequests
                }
                eventName="Meeting"
                cardTitle={title}
                meetingViaCalendly={meetingViaCalendly}
                liaison={liaison}
                buttonUrl={buttonUrl}
                isIntroCard={isIntroCard}
                serviceRequestsPageCard={true}
                {...props}
              />
            ) : isRequestPending ||
              (cancelledByCoach && !serviceRequest?.completed) ? (
              <MeetingCancelled
                meetingViaCalendly={meetingViaCalendly}
                coachName={coachName}
                buttonUrl={buttonUrl}
                hasCompletedReoccurringRequests={
                  hasCompletedReoccurringRequests
                }
                requestCreatedAt={requestCreatedAt}
                select={select}
                requestDueAt={requestDueAt}
                strengthsCode={strengthsCode}
                requestType={requestType}
                introCallServiceType={introCallServiceType}
                coachChange={coachChange}
                meetingIsCancelled={meetingIsCancelled}
                needsScheduling={needsScheduling}
              />
            ) : requestComplete || returnedStatus ? (
              <>
                <div className="flex items-center gap-4">
                  <LinkButton
                    href={buttonUrl}
                    variant={select ? 'outline' : 'coachcta'}
                    disabled={!buttonUrl}
                  >
                    {hasCompletedReoccurringRequests
                      ? buttonText
                      : t('view_feedback')}
                  </LinkButton>
                  {hasCompletedReoccurringRequests && (
                    <ViewPreviousRequests
                      buttonUrl={buttonUrl}
                      hasCompletedReoccurringRequests={
                        hasCompletedReoccurringRequests
                      }
                      isIntroCard={isIntroCard}
                    />
                  )}
                </div>
                {!serviceRequest.feedbacked && (
                  <div className="mt-4">
                    <FeedbackCarousel
                      serviceRequestId={serviceRequest.id}
                      serviceRequestName={serviceRequest.name}
                      serviceRequestType={serviceRequest.type}
                      className={disableLeftAlignment ? undefined : 'text-left'}
                    />
                  </div>
                )}
              </>
            ) : buttonChild && buttonChildShow ? (
              React.createElement(buttonChild, { buttonUrl })
            ) : (
              <div className="flex items-center gap-4">
                <LinkButton
                  variant={select ? 'outline' : 'coachcta'}
                  disabled={!buttonUrl}
                  href={buttonUrl}
                >
                  {noShow ? t('reschedule_meeting') : buttonText}
                </LinkButton>

                {hasCompletedReoccurringRequests && (
                  <ViewPreviousRequests
                    buttonUrl={buttonUrl}
                    hasCompletedReoccurringRequests={
                      hasCompletedReoccurringRequests
                    }
                    isIntroCard={isIntroCard}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <PhotoDiv
          photoUrl={photoUrl}
          coachCard={coachCard}
          activeCoachId={activeCoachId}
          serviceRequestCoachId={serviceRequestCoachId}
        />
      </div>
    </Wrapper>
  )
}

const PhotoDiv = ({
  photoUrl,
  coachCard,
  activeCoachId,
  serviceRequestCoachId,
}) => {
  if (!photoUrl) return null
  if (activeCoachId != serviceRequestCoachId) return null

  if (!coachCard)
    return (
      <div
        style={{ backgroundImage: `url(${photoUrl})` }}
        className={`bg-contain bg-center bg-no-repeat ${
          coachCard
            ? 'w-[300px] md:-mt-[30px] md:h-[130%]'
            : 'h-full w-60 object-cover'
        }`}
      />
    )
  return (
    <div className="pr-6">
      <HeadshotCircle coachImage={photoUrl} />
    </div>
  )
}

const MeetingCancelled = ({
  meetingViaCalendly,
  coachName,
  buttonUrl,
  hasCompletedReoccurringRequests,
  requestCreatedAt,
  requestDueAt,
  select,
  requestType,
  isIntroCard,
  coachChange,
  meetingIsCancelled,
  needsScheduling,
}) => {
  const { t } = useTranslation('service_card')
  const formatTime = useFormatTime()

  return meetingViaCalendly && meetingIsCancelled ? (
    <div>
      {coachChange ? (
        <p>{t('coach_change_description', { coachName })}</p>
      ) : (
        <p>{t('meeting_cancelled_description', { coachName })}</p>
      )}
      <div className="flex items-center gap-4">
        <LinkButton
          href={buttonUrl}
          variant={select ? 'outline' : 'coachcta'}
          disabled={!buttonUrl}
        >
          {t('reschedule_your_coach_meeting')}
        </LinkButton>
        {hasCompletedReoccurringRequests && (
          <ViewPreviousRequests
            buttonUrl={buttonUrl}
            hasCompletedReoccurringRequests={hasCompletedReoccurringRequests}
            isIntroCard={isIntroCard}
          />
        )}
      </div>
    </div>
  ) : (
    <div>
      <p>
        {requestType === 'ServiceRequest::StrengthsAssessment'
          ? t('strengths_code_request_submitted')
          : needsScheduling
            ? t('needs_scheduling')
            : t('request_submitted', {
                createdAt: formatTime(requestCreatedAt, 'short'),
                updateBy: formatTime(requestDueAt, 'short'),
              })}
      </p>
      <div className="flex items-center gap-4">
        <LinkButton
          href={buttonUrl}
          variant={select ? 'outline' : 'coachcta'}
          disabled={!buttonUrl}
        >
          {t('view_details')}
        </LinkButton>
        {hasCompletedReoccurringRequests && (
          <ViewPreviousRequests
            buttonUrl={buttonUrl}
            hasCompletedReoccurringRequests={hasCompletedReoccurringRequests}
            isIntroCard={isIntroCard}
          />
        )}
      </div>
    </div>
  )
}

const IconSection = ({ requestComplete, icons, iconType }) => {
  const IconComponent = icons[iconType]

  const { select } = useBenefits()

  const iconBg = select ? 'bg-white' : 'bg-intoo-green-coachcta'
  if (requestComplete)
    return (
      <img
        className="-mr-4 w-20"
        src="https://www-yns-com.s3.us-east-2.amazonaws.com/articles/images/article-complete.gif"
        alt="Request Complete"
      />
    )

  return (
    IconComponent && (
      <div className={cn('rounded-full p-3', iconBg)}>
        <IconComponent
          strokecolor={'currentColor'}
          className={cn('h-5 w-5', select ? 'text-black' : 'text-white')}
        />
      </div>
    )
  )
}

const StatusIndicator = ({
  requestCompletedAt,
  requestStatus,
  calendlyEvent,
  coachStarted,
  select,
}) => {
  const { t } = useTranslation('service_card')
  const backgroundColorClass = select
    ? 'bg-intoo-blue-bright'
    : 'bg-intoo-green-coachcta'
  const meetingIsCancelled = requestStatus === 'cancelled'
  const formatTime = useFormatTime()

  let text = ''

  if (requestCompletedAt) {
    const date = formatTime(requestCompletedAt, 'short')
    text = t('completed') + ' ' + date
  } else if (meetingIsCancelled) {
    text = t('meeting_cancelled')
  } else if (calendlyEvent?.service_request_no_show_id) {
    text = t('no_show')
  } else if (calendlyEvent && coachStarted) {
    const { start_time } = calendlyEvent
    const date = formatTime(start_time, 'short')
    text = t('call_scheduled_by_coach', { date })
  } else if (calendlyEvent) {
    const { start_time } = calendlyEvent
    const date = formatTime(start_time, 'short')
    text = t('call_scheduled', { date })
  }

  if (calendlyEvent || requestCompletedAt || meetingIsCancelled) {
    return (
      <div className={cn('rounded-[5px] p-2 text-white', backgroundColorClass)}>
        {text}
      </div>
    )
  }
  return null
}

const Wrapper = ({ className, children }) => {
  return <div className={className}>{children}</div>
}

export const ViewPreviousRequests = ({
  buttonUrl,
  hasCompletedReoccurringRequests,
  isIntroCard,
}) => {
  const { t } = useTranslation('service_card')
  return (
    hasCompletedReoccurringRequests && (
      <a
        href={
          buttonUrl
            ? buttonUrl +
              (buttonUrl.includes('?') ? '&' : '?') +
              'view_previous=true'
            : ''
        }
        className={
          isIntroCard
            ? 'text-link text-white !underline hover:font-bold hover:text-white'
            : 'text-link'
        }
      >
        {t('view_coach_feedback')}
      </a>
    )
  )
}
